import { Route, Routes } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
//import { TouchBackend } from 'react-dnd-touch-backend';

import MatchupPage from "./pages/Matchup";
import AddPlayerPage from "./pages/AddPlayer";
import SignupPage from "./pages/Signups";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import PlayerPage from "./pages/Player";
import HomePage from "./pages/HomePage";
import GoogleLoginPage from "./pages/GoogleLoginPage";
import MembershipSelectionPage from "./pages/MembershipSelection";
import OnboardingPage from "./pages/OnboardingPage";
import ManagerSubscriptionPage from "./pages/ManagerSubscription";
import MainPage from "./pages/MainPage";
import SettingsPage from "./pages/SettingsPage";
import LessonPage from "./pages/LessonRegistration";
import LessonSignupPage from "./pages/LessonSignups";
import AddLessonPlayerPage from "./pages/AddLessonPlayer";
import LessonManagePricePage from "./pages/LessonManagePricesPage";
import isSubscriber from "./components/membership/isSubscriber";
import PasswordResetPage from "./pages/PasswordResetPage";
import ContactPage from "./pages/ContactPage";
import hasPlayerSignegWaiver from "./components/PlayerManagement/hasPlayerSignedWaiver";
import getWaiverLinks from "./components/Settings/getWaiverLinks";
import AnalyticsPage from "./pages/Analytics.tsx";
import MarketingPage from "./pages/MarketingPage";
import AnalyticsRevamp from "./pages/AnalyticsRevamp";
import TournamentSignups from "./pages/TournamentSignups";
import TournamentCalendar from "./pages/TournamentCalendar";
import AddTournamentTeam from "./pages/AddTournamentTeam";
import AddTournamentPlayer from "./pages/AddTournamentPlayer";
import HorseManagementPage from "./pages/HorseManagement.tsx";
import { useScrollToTop } from "./hooks/useScrollToTop";
import SessionContext from "./components/layout/SessionContext";

import updateLocale from "dayjs/plugin/updateLocale";
import dayjs from "dayjs";
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1
});

const CLUB_ID_STORAGE_KEY = "cb_clubId";
const LAST_MANUAL_CLUB_STORAGE_KEY = "cb_lastManualClubId";
const GAME_ID_STORAGE_KEY = "cb_gameId";
const PLAYER_BOARD_STORAGE_KEY = "cb_playerBoard";

function readStoredPlayerBoard() {
  try {
    const value = Number(sessionStorage.getItem(PLAYER_BOARD_STORAGE_KEY));
    return sessionStorage.getItem(PLAYER_BOARD_STORAGE_KEY) !== null &&
      Number.isFinite(value) &&
      value > -1
      ? value
      : -1;
  } catch {
    return -1;
  }
}

function readStoredClubId() {
  try {
    const raw = sessionStorage.getItem(CLUB_ID_STORAGE_KEY);
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : -1;
  } catch {
    return -1;
  }
}

function readStoredLastManualClubId() {
  try {
    const raw = sessionStorage.getItem(LAST_MANUAL_CLUB_STORAGE_KEY);
    if (raw == null || raw === "") return -1;
    const value = Number(raw);
    return Number.isFinite(value) ? value : -1;
  } catch {
    return -1;
  }
}

function readStoredGameId() {
  try {
    const raw = sessionStorage.getItem(GAME_ID_STORAGE_KEY);
    if (raw == null || raw === "") return undefined;
    const asNumber = Number(raw);
    return Number.isFinite(asNumber) ? asNumber : raw;
  } catch {
    return undefined;
  }
}

function App() {
  //const [token, setToken] = useState();
  const [gameId, setGameId] = useState(readStoredGameId);
  const [clubId, setClubId] = useState(readStoredClubId); //7); //TODO update to -1
  const [lastManualClubId, setLastManualClubId] = useState(readStoredLastManualClubId);
  const selectClubManually = useCallback((nextClubId) => {
    setClubId(nextClubId);
    setLastManualClubId(nextClubId);
  }, []);
  const [gameDate, setGameDate] = useState();
  const [gamePoloType, setGamePoloType] = useState();
  const [gameDivisions, setGameDivisions] = useState([]);
  //const [userName, setUserName] = useState(window.reactPress.usermeta.nickname); //'peteymcgees');
  const [userName, setUserName] = useState(""); //peteymcgees"); //'peteymcgees');
  const [currentMembership, setCurrentMembership] = useState("");
  const [lessonPro, setLessonPro] = useState();
  const [subscriptionMode, setSubscriptionMode] = useState("create");
  const devMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [betaMode, setBetaMode] = useState(devMode);
  const session = useMemo(
    () => ({
      userName,
      currentMembership,
      clubId,
      setClubId,
      lastManualClubId,
      selectClubManually,
      betaMode,
    }),
    [userName, currentMembership, clubId, lastManualClubId, selectClubManually, betaMode]
  );
  const [playerBoard, setPlayerBoard] = useState(readStoredPlayerBoard);
  useScrollToTop();

  useEffect(() => {
    try {
      if (playerBoard != null && playerBoard > -1) {
        sessionStorage.setItem(PLAYER_BOARD_STORAGE_KEY, String(playerBoard));
      } else {
        sessionStorage.removeItem(PLAYER_BOARD_STORAGE_KEY);
      }
    } catch {
      // ignore sessionStorage failures
    }
  }, [playerBoard]);

  useEffect(() => {
    try {
      if (clubId != null && clubId > 0) {
        sessionStorage.setItem(CLUB_ID_STORAGE_KEY, String(clubId));
      } else {
        sessionStorage.removeItem(CLUB_ID_STORAGE_KEY);
      }
    } catch {
      // ignore sessionStorage failures (private mode, etc.)
    }
  }, [clubId]);

  useEffect(() => {
    try {
      if (lastManualClubId != null && Number.isFinite(lastManualClubId)) {
        sessionStorage.setItem(LAST_MANUAL_CLUB_STORAGE_KEY, String(lastManualClubId));
      }
    } catch {
      // ignore sessionStorage failures
    }
  }, [lastManualClubId]);

  useEffect(() => {
    try {
      if (gameId != null && gameId !== "") {
        sessionStorage.setItem(GAME_ID_STORAGE_KEY, String(gameId));
      } else {
        sessionStorage.removeItem(GAME_ID_STORAGE_KEY);
      }
    } catch {
      // ignore sessionStorage failures
    }
  }, [gameId]);
  /*console.log = (message) => {
    if (betaMode) {
      console.info(message);
    }
  };/*
  //const [userName, setUserName] = useState(devMode ? 'peteymcgees' : window.reactPress.usermeta.nickname); //'peteymcgees');
  //const [userName, setUserName] = useState('peteymcgees'); //'peteymcgees');
  /*const darkTheme = createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: "#0d9cc3",
      },
      secondary: {
        main: "#f50057",
      },
      divider: "rgba(36,133,216,0.44)",
      background: {
        paper: "#463f3f",
        default: "#272525",
      },
      text: {
        secondary: "rgba(17,203,226,0.7)",
      },
    },
  });*/
  /*const tokenName = "token_chukkerbook";
  function setToken(userToken) {
    console.log("SETTING TOKEN");
    console.log(JSON.stringify(userToken));
    localStorage.setItem(tokenName, JSON.stringify(userToken));
    window.location.reload(false);
  }

  function getToken() {
    const tokenString = localStorage.getItem(tokenName);
    const userToken = JSON.parse(tokenString);
    return userToken?.token;
  }*/
  /*useEffect(() => {
    const token = getToken();
    if (token) {
      console.log("TOKEN");
      console.log(token);
      setCurrentMembership("player");
      setUserName(token);
    }
  }, []);*/
  const chukkerTheme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#5D8AA8",
      },
      secondary: {
        main: "#800020",
      },
      background: {
        default: "#5D8AA8",
      },
      success: {
        main: "#A2D69A",
      },
      error: {
        main: "#800020",
      },
      warning: {
        main: "#F5C242",
      },
    },
    typography: {
      button: {
        // Here is where you can customise the button
        textTransform: "none",
      },
    },
  });
  const mainTheme = chukkerTheme;

  //const devPort = process.env.REACT_APP_MANAGER_DASHBOARD
  //  ? "https://www.chukkerbook.com:5002/"
  //  : "http://127.0.0.1:5000/";

  //const devPort = 'http://164.92.81.156:5000/'
  //const devPort = '/'
  //const devPort = 'https://172.17.0.2:5000/'
  //const devPort = 'https://www.chukkerbook.com:5002/'
  const devPort = devMode
    ? "http://127.0.0.1:5002/"
    : "https://api.react.chukkerbook.com/";
  //const devPort = "http://127.0.0.1:5000/"
  console.log(window.location);
  const [lessonPrice, setLessonPrice] = useState();
  const [leasePrice, setLeasePrice] = useState();
  const [pricingTab, setPricingTab] = useState("Lease");
  const [availableHorseForLease, setAvailableHorseForLease] = useState(0);
  const [eventDetails, setEventDetails] = useState(null);
  //const MANAGER_DASHBOARD = devMode || (process.env.REACT_APP_MANAGER_DASHBOARD !== "false"); //true
  //const MANAGER_DASHBOARD = true //process.env.REACT_APP_MANAGER_DASHBOARD !== "false"; //true
  useEffect(() => {
    fetch(devPort + "get_cookies", {
      credentials: "include",
      mode: "cors",
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data) {
          console.log("COOKIE COOKIE");
          if (data === "Expired session" || data === "No cookies found") {
            console.log(data);
            //setUserName("");
            //setCurrentMembership("");
          } else {
            setUserName(data);
          }
        /*} else {
          setUserName("");
          setCurrentMembership("");*/
        }
      });
  }, [devPort]);
  useEffect(() => {
    if (userName) {
      isSubscriber(devPort, userName, setCurrentMembership);
    }
  }, [devPort, userName]);
  const [alertMessageSuccess, setAlertMessageSuccess] = useState(false);
  const [waiverNotifications, setWaiverNotifications] = useState(0);
  const getNumberMissingWaiver = useCallback(() => {
    const fetch_missing_waiver =
      devPort + "club_number_missing_waiver/" + clubId;
    console.log(fetch_missing_waiver);
    fetch(fetch_missing_waiver)
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        console.log("TOP ELVE NOTIC");
        if (data && data[0]) {
          console.log(data[0][0]);
          setWaiverNotifications(data[0][0]);
          //  setRestrictedAccess(data[0][0]);
        }
        console.log(data);
      });
  }, [devPort, clubId]);

  const [waiverLinks, setWaiverLinks] = useState([]);
  useEffect(() => {
    if (clubId > -1) {
      if (playerBoard === -1) {
        getNumberMissingWaiver();
      } else {
        getWaiverLinks(devPort, clubId, setWaiverLinks);
      }
    }
  }, [clubId, devPort, playerBoard, getNumberMissingWaiver]);
  useEffect(() => {
    if (waiverLinks.length > 0) {
      hasPlayerSignegWaiver(devPort, clubId, playerBoard, (value) => {
        if (value) {
          setWaiverNotifications(0);
        } else {
          setWaiverNotifications(1);
        }
      });
    }
  }, [waiverLinks, devPort, clubId, playerBoard]);
  return (
    <SessionContext.Provider value={session}>
    <ThemeProvider theme={mainTheme}>
      <CssBaseline />
      <DndProvider backend={HTML5Backend}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Routes>
            {[
              { path: "/club", mode: "manager" },
              { path: "/game", mode: "manager" },
              { path: "/playerclubselection", mode: "player" },
              { path: "/playergame", mode: "player" },
            ].map(({ path, mode }) => (
              <Route
                key={path}
                exact
                path={path}
                element={
                  <HomePage
                    mode={mode}
                    devPort={devPort}
                    userName={userName}
                    clubId={clubId}
                    setClubId={setClubId}
                    setPlayerBoard={setPlayerBoard}
                    betaMode={betaMode}
                    waiverNotifications={waiverNotifications}
                    setSubscriptionMode={setSubscriptionMode}
                    setEventId={setGameId}
                    setLessonPro={setLessonPro}
                    setLessonPrice={setLessonPrice}
                    setLeasePrice={setLeasePrice}
                    setGameDate={setGameDate}
                    setGamePoloType={setGamePoloType}
                    setGameDivisions={setGameDivisions}
                    setEventDetails={setEventDetails}
                    setPricingTab={setPricingTab}
                  />
                }
              />
            ))}
            <Route
              exact
              path="/lesson"
              element={
                <LessonPage
                  setEventId={setGameId}
                  clubId={clubId}
                  setGameDate={setGameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                  setLessonPro={setLessonPro}
                  setLessonPrice={setLessonPrice}
                  betaMode={betaMode}
                  setPricingTab={setPricingTab}
                />
              }
            />
            <Route
              path="/player"
              element={
                <PlayerPage
                  clubId={clubId}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  setWaiverNotifications={setWaiverNotifications}
                  managerMode={-1}
                  betaMode={betaMode}
                />
              }
            />
            <Route
              path="/signup"
              element={
                <SignupPage
                  eventId={gameId}
                  clubId={clubId}
                  gameDate={gameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={-1}
                  setAvailableHorseForLease={setAvailableHorseForLease}
                  gameDivisions={gameDivisions}
                  userName={userName}
                />
              }
            />
            <Route
              path="/lessonsignup"
              element={
                <LessonSignupPage
                  eventId={gameId}
                  clubId={clubId}
                  gameDate={gameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                  betaMode={betaMode}
                  userName={userName}
                />
              }
            />
            <Route
              path="/lessonmanagepricing"
              element={
                <LessonManagePricePage
                  clubId={clubId}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  pricingTabDefault={pricingTab}
                />
              }
            />
            <Route
              path="/matchup"
              element={
                <MatchupPage
                  eventId={gameId}
                  clubId={clubId}
                  gameDate={gameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={-1}
                  gamePoloType={gamePoloType}
                  gameDivisions={gameDivisions}
                  betaMode={betaMode}
                />
              }
            />
            <Route
              path="/addplayer"
              element={
                <AddPlayerPage
                  gameId={gameId}
                  clubId={clubId}
                  gameDate={gameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={-1}
                  leasePrice={leasePrice}
                  setLeasePrice={setLeasePrice}
                  setManagerMode={setPlayerBoard}
                  setClubId={setClubId}
                  setGameId={setGameId}
                  availableHorseForLease={availableHorseForLease}
                  gameDivisions={gameDivisions}
                />
              }
            />
            <Route
              path="/addlessonplayer"
              element={
                <AddLessonPlayerPage
                  gameId={gameId}
                  clubId={clubId}
                  gameDate={gameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                  setManagerMode={setPlayerBoard}
                  lessonPro={lessonPro}
                  setClubId={setClubId}
                  setGameId={setGameId}
                  lessonPrice={lessonPrice}
                  setLessonPrice={setLessonPrice}
                  betaMode={betaMode}
                />
              }
            />
            <Route
              path="/tournamentsignups"
              element={
                <TournamentSignups
                  eventId={gameId}
                  clubId={clubId}
                  TournamentDate={gameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                  betaMode={betaMode}
                  eventDetails={eventDetails}
                  setEventDetails={setEventDetails}
                />
              }
            />
            <Route
              path="/tournamentcalendar"
              element={
                <TournamentCalendar
                  devPort={devPort}
                  clubId={clubId}
                  managerMode={playerBoard}
                  waiverNotifications={waiverNotifications}
                />
              }
            />
            <Route
              path="/addtournamentteam"
              element={
                <AddTournamentTeam
                  eventId={gameId}
                  clubId={clubId}
                  TournamentDate={gameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                  betaMode={betaMode}
                  eventDetails={eventDetails}
                  setEventDetails={setEventDetails}
                />
              }
            />
            <Route
              path="/addtournamentplayer"
              element={
                <AddTournamentPlayer
                  eventId={gameId}
                  clubId={clubId}
                  TournamentDate={gameDate}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                  betaMode={betaMode}
                  eventDetails={eventDetails}
                  setEventDetails={setEventDetails}
                />
              }
            />
            <Route
              exact
              path="/account-login"
              element={
                <GoogleLoginPage
                  devPort={devPort}
                  userName={userName}
                  setUserName={setUserName}
                  setPlayerBoard={setPlayerBoard}
                  newSignUp={false}
                  setCurrentMembership={setCurrentMembership}
                  alertMessageSuccess={alertMessageSuccess}
                  setAlertMessageSuccess={setAlertMessageSuccess}
                  betaMode={betaMode}
                  //setToken={setToken}
                />
              }
            />
            <Route
              exact
              path="/account-signup"
              element={
                <GoogleLoginPage
                  devPort={devPort}
                  userName={userName}
                  setUserName={setUserName}
                  setPlayerBoard={setPlayerBoard}
                  newSignUp={true}
                  setCurrentMembership={setCurrentMembership}
                  betaMode={betaMode}
                  //setToken={setToken}
                />
              }
            />
            <Route
              exact
              path="/account-reset"
              element={
                <PasswordResetPage
                  devPort={devPort}
                  setAlertMessageSuccess={setAlertMessageSuccess}
                />
              }
            />
            <Route
              exact
              path="/a"
              element={
                <MainPage
                  userName={userName}
                  setUserName={setUserName}
                  devPort={devPort}
                  currentMembership={currentMembership}
                  alertMessageSuccess={alertMessageSuccess}
                  setAlertMessageSuccess={setAlertMessageSuccess}
                  setBetaMode={setBetaMode}
                />
              }
            />
            <Route
              exact
              path="/"
              element={
                <MarketingPage
                  userName={userName}
                  setUserName={setUserName}
                  devPort={devPort}
                  currentMembership={currentMembership}
                  alertMessageSuccess={alertMessageSuccess}
                  setAlertMessageSuccess={setAlertMessageSuccess}
                  setBetaMode={setBetaMode}
                />
              }
            />
            <Route
              path="/selectmembership"
              element={
                <MembershipSelectionPage
                  devPort={devPort}
                  currentMembership={currentMembership}
                  setCurrentMembership={setCurrentMembership}
                  userName={userName}
                  setUserName={setUserName}
                />
              }
            />
            {["player", "manager"].map((mode) => (
              <Route
                key={mode}
                path={"/onboarding/" + mode}
                element={
                  <OnboardingPage
                    key={mode}
                    mode={mode}
                    devPort={devPort}
                    userName={userName}
                    setUserName={setUserName}
                    setCurrentMembership={setCurrentMembership}
                    betaMode={betaMode}
                  />
                }
              />
            ))}
            <Route
              path="/managersubscription"
              element={
                <ManagerSubscriptionPage
                  devPort={devPort}
                  userName={userName}
                  setUserName={setUserName}
                  setCurrentMembership={setCurrentMembership}
                  clubId={clubId}
                  subscriptionMode={subscriptionMode}
                />
              }
            />
            <Route
              path="/user-settings"
              element={
                <SettingsPage
                  devPort={devPort}
                  userName={userName}
                  currentMembership={currentMembership}
                  setCurrentMembership={setCurrentMembership}
                  setSubscriptionMode={setSubscriptionMode}
                  setClubId={setClubId}
                />
              }
            />
            <Route
              exact
              path="/playerclub"
              element={
                <PlayerPage
                  clubId={clubId}
                  devPort={devPort}
                  managerMode={playerBoard}
                  waiverNotifications={waiverNotifications}
                  betaMode={betaMode}
                />
              }
            />
            <Route
              path="/playersignup"
              element={
                <SignupPage
                  eventId={gameId}
                  clubId={clubId}
                  gameDate={gameDate}
                  devPort={devPort}
                  managerMode={playerBoard}
                  waiverNotifications={waiverNotifications}
                  setAvailableHorseForLease={setAvailableHorseForLease}
                  gameDivisions={gameDivisions}
                />
              }
            />
            <Route
              path="/playermatchup"
              element={
                <MatchupPage
                  eventId={gameId}
                  clubId={clubId}
                  gameDate={gameDate}
                  devPort={devPort}
                  managerMode={playerBoard}
                  waiverNotifications={waiverNotifications}
                  betaMode={betaMode}
                  gamePoloType={gamePoloType}
                  gameDivisions={gameDivisions}
                />
              }
            />
            <Route
              path="/playerregister"
              element={
                <AddPlayerPage
                  gameId={gameId}
                  clubId={clubId}
                  gameDate={gameDate}
                  devPort={devPort}
                  managerMode={playerBoard}
                  waiverNotifications={waiverNotifications}
                  leasePrice={leasePrice}
                  setLeasePrice={setLeasePrice}
                  setManagerMode={setPlayerBoard}
                  setClubId={setClubId}
                  setGameId={setGameId}
                  availableHorseForLease={availableHorseForLease}
                  gameDivisions={gameDivisions}
                />
              }
            />
            <Route
              path="/contact"
              element={
                <ContactPage
                  devPort={devPort}
                  setAlertMessageSuccess={setAlertMessageSuccess}
                />
              }
            />
            <Route
              path="/analytics"
              element={
                <AnalyticsPage
                  clubId={clubId}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                />
              }
            />
            <Route
              path="/analyticsRevamp"
              element={
                <AnalyticsRevamp
                  clubId={clubId}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                />
              }
            />
            <Route
              path="/horses"
              element={
                <HorseManagementPage
                  clubId={clubId}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={-1}
                  userName={userName}
                />
              }
            />
            <Route
              path="/playerhorses"
              element={
                <HorseManagementPage
                  clubId={clubId}
                  devPort={devPort}
                  waiverNotifications={waiverNotifications}
                  managerMode={playerBoard}
                  userName={userName}
                />
              }
            />
          </Routes>
        </LocalizationProvider>
      </DndProvider>
    </ThemeProvider>
    </SessionContext.Provider>
  );
}

export default App;
